// frontend/app/components/lessonPlans/LessonVideoPlayer.js
//
// A self-contained HLS video player for lessons, matching LessonPwoPlayer.
//
// Like the PWO case, `handleVideoClick` cannot be reused: it flips
// `activeModule`, drives one shared <video> through `videoRef`, and is torn
// down by the navigate-away cleanup effect. This component owns its own
// <video> and hls.js instance and reads no global video state, so the clip
// plays inline in the step and nothing else can stop it.
//
// It opens at the lesson's tagged moment — and stops at the end of that moment,
// which is the other half of what a tag-level reference means. A tag names a
// segment of a longer recording, so playing past its end walks the class into
// material that belongs to a later step of the lesson.
//
// "Stops" here means pause and say so, not hide the player. A video that simply
// freezes mid-sentence reads as buffering or a crash to a teacher standing in
// front of a class, and the honest recovery — carry on into the next segment —
// is not discoverable from a frozen frame. So the segment end raises a small
// panel naming what finished, offering a replay and a deliberate way to keep
// watching. The teacher stays in charge; the default just stops being wrong.

import React, { useEffect, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Hls from "hls.js";

import { attachSegmentStop } from "./lessonVideoSegmentStop";
import { resolveLessonVideoSource } from "./lessonVideoSource";

const NATIVE_WIDTH = 1280;
const NATIVE_HEIGHT = 720;

const LessonVideoPlayer = ({
  sourceVideoId,
  tagId = null,
  authToken,
  languageId = 1,
  isDarkMode = false,
  onClose,
  t,
}) => {
  const translate = t || ((value) => value);

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState(null);

  // The resolved segment, kept in state so the stop effect below can see it.
  // `endSec: null` is a real answer meaning "runs to the end of the recording".
  const [segment, setSegment] = useState(null);
  const [segmentEnded, setSegmentEnded] = useState(false);
  // True only when the browser refused to start on its own. The element keeps
  // its own controls either way; this exists so the surface can say why nothing
  // is happening rather than showing a still frame and no reason.
  const [needsPress, setNeedsPress] = useState(false);

  useEffect(() => {
    let disposed = false;

    const destroyHls = () => {
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch {}
        hlsRef.current = null;
      }
    };

    (async () => {
      setStatus("loading");
      setErrorMessage(null);
      setSegment(null);
      setSegmentEnded(false);
      destroyHls();

      let source = null;
      try {
        source = await resolveLessonVideoSource({
          videoId: sourceVideoId,
          tagId,
          languageId,
          authToken,
          // Lesson tags are authored deliberately; open exactly where the
          // author pointed rather than skipping a title card.
          minimumStartSec: 0,
        });
      } catch (error) {
        if (disposed) return;
        console.error("[LESSON VIDEO] could not resolve source:", error);
        setStatus("error");
        setErrorMessage(error?.message || "Could not load this video.");
        return;
      }

      if (disposed) return;

      const element = videoRef.current;
      if (!element) {
        setStatus("error");
        setErrorMessage("The video player failed to mount.");
        return;
      }

      const seekToTag = () => {
        try {
          if (source.startSec > 0) element.currentTime = source.startSec;
        } catch {}
        if (!disposed) {
          setSegment({
            startSec: source.startSec,
            endSec: source.endSec ?? null,
            tagName: source.tagName || null,
          });
          setStatus("ready");

          // The teacher already pressed once to open this; a paused first frame
          // makes them press again while the class watches. Autoplay can still
          // be refused — sound needs user activation, and the three round trips
          // it took to resolve this stream can outlast it — so a refusal just
          // means the native play button does the job, and is recorded rather
          // than thrown.
          try {
            const started = element.play?.();
            if (started && typeof started.catch === "function") {
              started.catch(() => {
                if (!disposed) setNeedsPress(true);
              });
            }
          } catch {
            if (!disposed) setNeedsPress(true);
          }
        }
      };

      if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true });
        hlsRef.current = hls;
        hls.loadSource(source.playlistUrl);
        hls.attachMedia(element);
        hls.on(Hls.Events.MANIFEST_PARSED, seekToTag);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data?.fatal || disposed) return;
          console.error("[LESSON VIDEO] fatal hls error:", data);
          setStatus("error");
          setErrorMessage("Playback failed for this video.");
        });
      } else if (element.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari plays HLS natively.
        element.src = source.playlistUrl;
        element.addEventListener("loadedmetadata", seekToTag, { once: true });
      } else {
        setStatus("error");
        setErrorMessage("This browser cannot play the lesson video format.");
      }
    })();

    return () => {
      disposed = true;
      destroyHls();
      const element = videoRef.current;
      if (element) {
        try {
          element.pause();
          element.removeAttribute("src");
          element.load();
        } catch {}
      }
    };
  }, [sourceVideoId, tagId, authToken, languageId]);

  // Stop at the end of the tagged segment. The rules live in
  // `lessonVideoSegmentStop` so they can be tested against a fake element; this
  // only has to turn them into what the teacher sees.
  useEffect(() => {
    if (status !== "ready") return undefined;
    return attachSegmentStop({
      element: videoRef.current,
      endSec: segment?.endSec,
      onEnded: () => setSegmentEnded(true),
      onCleared: () => setSegmentEnded(false),
    });
  }, [segment, status]);

  const replaySegment = () => {
    const element = videoRef.current;
    if (!element || !segment) return;
    try {
      element.currentTime = segment.startSec;
      setSegmentEnded(false);
      element.play?.();
    } catch {}
  };

  const continuePastSegment = () => {
    const element = videoRef.current;
    setSegmentEnded(false);
    try {
      element?.play?.();
    } catch {}
  };

  // Bring the clip into view when it opens. Web only — elsewhere (and under
  // jest-expo) a View ref is not a DOM node, so this is a no-op.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof node.scrollIntoView !== "function") return undefined;
    const raf = requestAnimationFrame(() => {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        node.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <View
      ref={containerRef}
      style={[styles.container, { borderColor: isDarkMode ? "#1e293b" : "#cbd5e1" }]}
    >
      <video
        ref={videoRef}
        controls
        playsInline
        style={{
          // Absolute for the same reason as the PWO iframe: keep the element
          // out of flow so aspectRatio governs the box.
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "block",
          backgroundColor: "#000",
        }}
      />

      {status !== "ready" && (
        <View style={styles.overlay} pointerEvents="none">
          {status === "error" ? (
            <>
              <MaterialCommunityIcons name="alert-circle-outline" size={28} color="#f87171" />
              <Text style={styles.overlayTitle}>
                {translate("This video could not be opened")}
              </Text>
              {errorMessage ? (
                <Text style={styles.overlayDetail}>{errorMessage}</Text>
              ) : null}
            </>
          ) : (
            <>
              <ActivityIndicator size="large" color="#38bdf8" />
              <Text style={styles.overlayTitle}>{translate("Loading video...")}</Text>
            </>
          )}
        </View>
      )}

      {status === "ready" && needsPress && !segmentEnded ? (
        // The browser would not start it for us. box-none so the native play
        // button underneath stays pressable — this is a label, not a barrier.
        <View style={styles.needsPress} pointerEvents="box-none">
          {/* Literal white, not a theme colour: this sits on its own dark
              scrim over video, so it is the same in either theme. */}
          <Text style={styles.needsPressText}>
            {translate("Press play to start the clip")}
          </Text>
        </View>
      ) : null}

      {status === "ready" && segmentEnded ? (
        // box-none so the tint never swallows a tap: only the card below takes
        // input, and the browser's own controls stay reachable underneath.
        <View style={styles.segmentEnd} pointerEvents="box-none">
          <View style={styles.segmentEndCard}>
            <Text style={styles.segmentEndTitle}>
              {translate("End of this clip")}
            </Text>
            <Text style={styles.segmentEndDetail} numberOfLines={2}>
              {segment?.tagName || translate("The lesson's clip has finished.")}
            </Text>
            <View style={styles.segmentEndActions}>
              <TouchableOpacity
                onPress={replaySegment}
                accessibilityRole="button"
                accessibilityLabel={translate("Replay this clip")}
                style={[styles.segmentEndButton, styles.segmentEndPrimary]}
              >
                <MaterialCommunityIcons name="replay" size={15} color="#0f172a" />
                <Text style={styles.segmentEndPrimaryText}>{translate("Replay")}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={continuePastSegment}
                accessibilityRole="button"
                accessibilityLabel={translate("Keep playing past this clip")}
                style={styles.segmentEndButton}
              >
                <MaterialCommunityIcons name="play" size={15} color="#e2e8f0" />
                <Text style={styles.segmentEndText}>{translate("Keep watching")}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      ) : null}

      {onClose ? (
        <TouchableOpacity
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={translate("Close video")}
          style={styles.close}
        >
          <MaterialCommunityIcons name="close" size={16} color="#ffffff" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 1100,
    alignSelf: "center",
    aspectRatio: NATIVE_WIDTH / NATIVE_HEIGHT,
    borderRadius: 14,
    borderWidth: 1,
    overflow: "hidden",
    backgroundColor: "#000",
    marginBottom: 12,
    position: "relative",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    backgroundColor: "rgba(2,6,23,0.82)",
  },
  overlayTitle: {
    color: "#e2e8f0",
    fontSize: 14,
    fontWeight: "700",
    marginTop: 10,
    textAlign: "center",
  },
  overlayDetail: {
    color: "#94a3b8",
    fontSize: 12,
    marginTop: 4,
    textAlign: "center",
  },
  // Sits above the control bar so it never covers the play button it is asking
  // the teacher to press.
  needsPress: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 64,
    alignItems: "center",
  },
  needsPressText: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: "600",
    backgroundColor: "rgba(0,0,0,0.62)",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    overflow: "hidden",
  },

  segmentEnd: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    // Clear of the browser's control bar, so the teacher can still scrub while
    // the panel is up.
    bottom: 56,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
  },
  segmentEndCard: {
    maxWidth: 360,
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(148,163,184,0.35)",
    backgroundColor: "rgba(2,6,23,0.92)",
  },
  segmentEndTitle: {
    color: "#f1f5f9",
    fontSize: 14,
    fontWeight: "700",
    textAlign: "center",
  },
  segmentEndDetail: {
    color: "#94a3b8",
    fontSize: 12,
    marginTop: 4,
    textAlign: "center",
  },
  segmentEndActions: {
    flexDirection: "row",
    gap: 8,
    marginTop: 12,
  },
  segmentEndButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(148,163,184,0.4)",
  },
  segmentEndPrimary: {
    backgroundColor: "#38bdf8",
    borderColor: "#38bdf8",
  },
  segmentEndPrimaryText: {
    color: "#0f172a",
    fontSize: 12,
    fontWeight: "700",
  },
  segmentEndText: {
    color: "#e2e8f0",
    fontSize: 12,
    fontWeight: "600",
  },
  close: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 20,
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(15,23,42,0.75)",
  },
});

export default LessonVideoPlayer;
